import { useIsTravelApp, wishlistsApi } from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionLink,
  pluralize,
  truncateString,
  WishlistHeartIconEntry,
  wishlistManagementModalActions,
} from "halifax";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import { goToAvailability } from "../../utils/queryStringHelpers";
import { IMobileDateSelectorVariant } from "../MobileDateSelector/component";
import { ConnectedMobileDateSelector } from "../MobileDateSelector/container";
import { IOccupanySelectionVariant } from "../OccupancySelector/component";
import { ConnectedOccupancySelector } from "../OccupancySelector/container";
import { MobileShopHeaderConnectorProps } from "./container";
import "./styles.scss";

export interface IMobileShopHeaderProps
  extends MobileShopHeaderConnectorProps,
    RouteComponentProps {}

export const MobileShopHeader = (props: IMobileShopHeaderProps) => {
  const {
    selectedListing,
    fromDate,
    untilDate,
    numAdults,
    numChildren,
    numPets,
    history,
  } = props;

  const expState = useExperiments();
  const dispatch = useDispatch();
  const { isTravelApp } = useIsTravelApp();

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const hasWishlists = Boolean(
    useSelector(wishlistsApi.endpoints.getAllWishlists.select())?.data
      ?.wishlists?.length
  );

  const title = selectedListing?.listing?.content?.name || "Stay";
  const maxOccupancy = selectedListing?.listing?.terms?.maxOccupancy || 16;

  const returnToAvailability = () => {
    goToAvailability({
      history,
      listing: selectedListing?.listing,
      fromDate,
      untilDate,
      adultsCount: numAdults,
      childrenCount: numChildren.length,
      petsCount: numPets,
    });
  };

  const separator = (
    <span
      style={{
        fontSize: "12px",
        fontWeight: 400,
        color: "#CECFCD",
      }}
    >
      |
    </span>
  );

  return (
    <Box
      className={clsx("mobile-shop-header", { "travel-app": isTravelApp })}
      display="flex"
      flexDirection="row"
      alignItems="center"
      borderBottom=".5px solid var(--grey-11)"
      height="55px"
      style={{ backgroundColor: "white" }}
      paddingX="18px"
    >
      {isTravelApp ? (
        <div className="back-button-placeholder" />
      ) : (
        <ActionLink
          onClick={returnToAvailability}
          content={
            <Box width="50px" height="44px" display="flex" alignItems="center">
              <FontAwesomeIcon icon={faChevronLeft} />
            </Box>
          }
        />
      )}

      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Typography variant="body1" style={{ textAlign: "center" }}>
          {truncateString(title, 40, true)}
        </Typography>
        <Box display="flex" style={{ gap: "5px" }} alignItems="center">
          <Typography
            style={{
              fontSize: "12px",
              fontWeight: "400",
              color: "#505050",
            }}
          >
            {fromDate &&
              untilDate &&
              `${dayjs(fromDate).format("MMM DD")} - ${dayjs(untilDate).format(
                "MMM DD"
              )}`}
          </Typography>
          {separator}

          <ConnectedOccupancySelector
            variant={IOccupanySelectionVariant.COMPONENT}
            maxOccupancy={maxOccupancy}
            component={
              <Typography
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "var(--blue-9)",
                  cursor: "pointer",
                }}
              >
                {`${numAdults + numChildren.length} ${pluralize(
                  numAdults + numChildren.length,
                  "traveler",
                  "travelers"
                )}
                `}
              </Typography>
            }
          />
        </Box>
      </Box>

      <Box
        minWidth="50px"
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent={isWishlistExperiment ? "space-between" : "flex-end"}
      >
        {isWishlistExperiment && (
          <WishlistHeartIconEntry
            hasWishlists={hasWishlists}
            onClick={() =>
              dispatch(wishlistManagementModalActions.openModal({}))
            }
          />
        )}

        <ConnectedMobileDateSelector
          variant={IMobileDateSelectorVariant.ICON}
        />
      </Box>
    </Box>
  );
};
